import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, Headphones, PenTool, Clock, Play, Sparkles, GraduationCap, Layers, ArrowRight, AlertTriangle, Check, Trophy, Target, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { questionService, type Question } from "@/services/questionService";
import { testStateService } from "@/services/testStateService";
import { getCompetencyLabel } from "@/data/tefScoreScale";
import { cn } from "@/lib/utils";
import { createTestSessionId } from "@/utils/testRandomizer";
import { isWritingCompetency } from "@/utils/questionSectionUtils";
import { useAuth } from "@/contexts/AuthContext";

export default function StartTest() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();

  // Load questions asynchronously on mount with synchronous memory backing fallback
  const [allQuestions, setAllQuestions] = useState<Question[]>(() => questionService.getCachedQuestionsSync());

  // Wizard States
  const [trainingType, setTrainingType] = useState<"competence" | "full_exam" | null>(null);
  const [selectedCompetence, setSelectedCompetence] = useState<"reading" | "listening" | "writing" | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [trainingMode, setTrainingMode] = useState<"practice" | "exam" | "diagnostic">("practice");
  const [questionCount, setQuestionCount] = useState<number | "all">("all");
  const [simulationMode, setSimulationMode] = useState<"short" | "standard">("standard");

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const list = await questionService.getQuestions();
        setAllQuestions(list);

        const compParam = searchParams.get("competence");
        if (compParam === "reading" || compParam === "listening" || compParam === "writing") {
          setTrainingType("competence");
          setSelectedCompetence(compParam);
        }
      } catch (e) {
        console.error("Erro ao buscar questões:", e);
      }
    };
    fetchQuestions();
  }, [searchParams]);

  // Handle mode change to clear question count if needed
  useEffect(() => {
    if (trainingMode === "exam" || trainingMode === "diagnostic") {
      setQuestionCount("all");
    }
  }, [trainingMode]);

  // Reset training mode if switching to full exam and diagnostic was selected
  useEffect(() => {
    if (trainingType === "full_exam" && trainingMode === "diagnostic") {
      setTrainingMode("exam");
    }
  }, [trainingType, trainingMode]);

  // When competence changes, reset section
  const handleSelectCompetence = (comp: "reading" | "listening" | "writing") => {
    setSelectedCompetence(comp);
    setSelectedSection(null);
    
    // Se mudar para EE e o modo for diagnóstico, mudar para modo prova (mais adequado)
    if (isWritingCompetency(comp) && trainingMode === "diagnostic") {
      setTrainingMode("exam");
    }
  };

  // Calculate global simulation counts from question bank
  const readingCount = allQuestions.filter(q => q.competence === "Reading").length;
  const listeningCount = allQuestions.filter(q => q.competence === "Listening").length;
  const writingCount = allQuestions.filter(q => q.competence === "Writing").length;

  // Reading sub-section configurations & availability
  const readingSections = questionService.getSectionCountsByCompetency("reading", allQuestions);
  const listeningSections = questionService.getSectionCountsByCompetency("listening", allQuestions);
  const writingSections = questionService.getSectionCountsByCompetency("writing", allQuestions);

  // Available count for the selected section
  const getSelectedSectionAvailableCount = () => {
    if (!selectedCompetence || !selectedSection) return 0;
    if (selectedSection === "all") {
      return selectedCompetence === "reading" ? readingCount : selectedCompetence === "listening" ? listeningCount : writingCount;
    }
    const list = selectedCompetence === "reading" ? readingSections : selectedCompetence === "listening" ? listeningSections : writingSections;
    const item = list.find(s => s.section === selectedSection);
    return item ? item.count : 0;
  };

  const availableCount = getSelectedSectionAvailableCount();

  // Button activation rules
  const canStart = () => {
    if (!trainingType) return false;
    if (trainingType === "full_exam") return true;
    if (trainingType === "competence") {
      if (!selectedCompetence) return false;
      if (!selectedSection) return false;
      if (availableCount === 0) return false;
      return true;
    }
    return false;
  };

  const handleStartTest = () => {
    if (!canStart()) return;

    // Free Trial Limits Enforcement Check
    if (profile && profile.role !== "admin" && profile.plan !== "premium") {
      if (profile.trial_tests_used >= profile.trial_tests_limit) {
        alert(t("startTest.trialLimitReached", "Seu período gratuito chegou ao limite. Assine para continuar usando todos os recursos."));
        navigate("/upgrade");
        return;
      }
    }

    // Clear any previous session state so the new test generates fresh shuffled questions
    testStateService.clearPartialProgress();
    
    function clearPreviousActiveTestSession() {
      const keysToRemove = [
        "activeTest",
        "currentTest",
        "testSession",
        "selectedQuestions",
        "testQuestions",
        "tef_active_test",
        "tef_current_test",
        "tef_test_session",
        "tef_selected_questions",
        "tef_current_test_attempt_id"
      ];

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    }
    
    clearPreviousActiveTestSession();

    const configStr = localStorage.getItem("tef_trainer_settings");
    const config = configStr ? JSON.parse(configStr) : {};
    // Forçamos o embaralhamento até a configuração se mostrar perfeitamente estável (shouldShuffle temporariamente forçado a true)
    const shuffleQuestions = true;
    const testSessionId = createTestSessionId();

    console.log("[StartTest] settings", config);
    console.log("[StartTest] shuffleQuestions from settings:", shuffleQuestions);
    console.log("[StartTest] NEW TEST SESSION:", testSessionId);

    if (trainingType === "full_exam") {
      const hasSectionA = writingSections.some(s => (s.key === "section_a" || s.section.toLowerCase().includes("section a")) && s.count > 0);
      const hasSectionB = writingSections.some(s => (s.key === "section_b" || s.section.toLowerCase().includes("section b")) && s.count > 0);
      if (!hasSectionA || !hasSectionB) {
        alert(t("startTest.insufficientEeFullExam", "Não há tarefas suficientes de Expression Écrite cadastradas para montar o simulado completo."));
        return;
      }
      navigate(`/test/runner?mode=full_exam&simulationMode=${simulationMode}&practiceMode=${trainingMode}`, {
        state: { testSessionId, shuffleQuestions, forceNewSession: true }
      });
    } else if (trainingType === "competence" && selectedCompetence) {
      if (selectedCompetence === "writing" && selectedSection === "all") {
        const hasSectionA = writingSections.some(s => (s.key === "section_a" || s.section.toLowerCase().includes("section a")) && s.count > 0);
        const hasSectionB = writingSections.some(s => (s.key === "section_b" || s.section.toLowerCase().includes("section b")) && s.count > 0);
        if (!hasSectionA || !hasSectionB) {
          alert(t("startTest.insufficientEeCompleteTest", "Não há tarefas suficientes de Expression Écrite cadastradas para o teste completo."));
          return;
        }
      }

      const diagnosticParam = trainingMode === "diagnostic" ? "&diagnosticMode=true" : "";

      if (selectedSection === "all") {
        navigate(`/test/runner?type=${selectedCompetence}&mode=complete&practiceMode=${trainingMode}&limit=${questionCount}${diagnosticParam}`, {
          state: { testSessionId, shuffleQuestions, forceNewSession: true }
        });
      } else {
        navigate(`/test/runner?type=${selectedCompetence}&mode=section&section=${encodeURIComponent(selectedSection)}&practiceMode=${trainingMode}&limit=${questionCount}${diagnosticParam}`, {
          state: { testSessionId, shuffleQuestions, forceNewSession: true }
        });
      }
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight text-gray-900">{t("startTest.title", "Iniciar Teste")}</h1>
        <p className="text-gray-500 mt-1 text-base">{t("startTest.subtitle", "Escolha como deseja treinar para o exame oficial do TEF Canada.")}</p>
      </div>

      {/* Trial Status Active Warning */}
      {profile && profile.role !== "admin" && profile.plan !== "premium" && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-between text-xs gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <span className="text-amber-800 font-semibold">
              {t("startTest.trialUsageMessage", "Você completou {{used}} de {{limit}} testes gratuitos disponíveis.", { used: profile.trial_tests_used, limit: profile.trial_tests_limit })}
            </span>
          </div>
          <Button size="sm" onClick={() => navigate("/upgrade")} className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-[10px] h-8 px-3 shrink-0">
            {t("startTest.btnUpgradeNow", "Assinar Premium")}
          </Button>
        </div>
      )}

      {/* Seção 1: Escolha o tipo de treino */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-gray-800">{t("startTest.trainingTypeTitle", "1. Escolha o tipo de treino")}</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Card Treino por Competência */}
          <Card 
            onClick={() => { setTrainingType("competence"); if(!selectedCompetence) setSelectedCompetence("reading"); }}
            className={cn(
              "cursor-pointer border-2 transition-all duration-300 overflow-hidden hover:shadow-md relative",
              trainingType === "competence" ? "border-primary bg-primary/5 shadow-md" : "border-gray-200 bg-white"
            )}
          >
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold">
                  <Target className="w-6 h-6" />
                </div>
                {trainingType === "competence" && (
                  <Badge className="bg-primary text-white rounded-full px-3 py-1">{t("common.ready", "Selecionado")}</Badge>
                )}
              </div>
              <CardTitle className="text-xl font-bold mt-4 text-gray-900">{t("startTest.competenceTitle", "Treino por Competência")}</CardTitle>
              <CardDescription className="text-sm text-gray-600 mt-1">
                {t("startTest.competenceDesc", "Pratique uma habilidade específica: leitura, escuta ou escrita com foco direcionado.")}
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Card Simulado Completo */}
          <Card 
            onClick={() => setTrainingType("full_exam")}
            className={cn(
              "cursor-pointer border-2 transition-all duration-300 overflow-hidden hover:shadow-md relative",
              trainingType === "full_exam" ? "border-primary bg-primary/5 shadow-md" : "border-gray-200 bg-white"
            )}
          >
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold">
                  <GraduationCap className="w-6 h-6" />
                </div>
                {trainingType === "full_exam" && (
                  <Badge className="bg-primary text-white rounded-full px-3 py-1">{t("common.ready", "Selecionado")}</Badge>
                )}
              </div>
              <CardTitle className="text-xl font-bold mt-4 text-gray-900">{t("startTest.fullExamTitle", "Simulado Completo")}</CardTitle>
              <CardDescription className="text-sm text-gray-600 mt-1">
                {t("startTest.fullExamDesc", "Faça uma sequência com CE, CO e EE para simular uma experiência mais completa de exame.")}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>

      {/* --- FLUXO: TREINO POR COMPETÊNCIA --- */}
      {trainingType === "competence" && (
        <div className="space-y-8 animate-in slide-in-from-top-4 duration-300 pt-2 border-t border-gray-200">
          {/* Seção 2: Escolha a competência */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-800">{t("startTest.competenceStepTitle", "2. Escolha a competência")}</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {/* CE */}
              <Card 
                onClick={() => handleSelectCompetence("reading")}
                className={cn(
                  "cursor-pointer border-2 transition-all duration-300 overflow-hidden hover:shadow-md relative",
                  selectedCompetence === "reading" ? "border-purple-600 bg-purple-50/40 shadow-md" : "border-gray-200 bg-white"
                )}
              >
                <CardHeader className="pb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 mb-2">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg font-bold text-gray-900">Compréhension Écrite (CE)</CardTitle>
                  <CardDescription className="text-xs text-purple-700 font-semibold uppercase tracking-wider mt-0.5">
                    {t("dashboard.studyNow", "Leitura")}
                  </CardDescription>
                  <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                    {t("startTest.readingDesc", "Textos, documentos, anúncios, tabelas e artigos de imprensa.")}
                  </p>
                </CardHeader>
              </Card>

              {/* CO */}
              <Card 
                onClick={() => handleSelectCompetence("listening")}
                className={cn(
                  "cursor-pointer border-2 transition-all duration-300 overflow-hidden hover:shadow-md relative",
                  selectedCompetence === "listening" ? "border-blue-600 bg-blue-50/40 shadow-md" : "border-gray-200 bg-white"
                )}
              >
                <CardHeader className="pb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 mb-2">
                    <Headphones className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg font-bold text-gray-900">Compréhension Orale (CO)</CardTitle>
                  <CardDescription className="text-xs text-blue-700 font-semibold uppercase tracking-wider mt-0.5">
                    {t("dashboard.studyNow", "Escuta")}
                  </CardDescription>
                  <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                    {t("startTest.listeningDesc", "Áudios, diálogos, anúncios públicos e mensagens orais.")}
                  </p>
                </CardHeader>
              </Card>

              {/* EE */}
              <Card 
                onClick={() => handleSelectCompetence("writing")}
                className={cn(
                  "cursor-pointer border-2 transition-all duration-300 overflow-hidden hover:shadow-md relative",
                  selectedCompetence === "writing" ? "border-emerald-600 bg-emerald-50/40 shadow-md" : "border-gray-200 bg-white"
                )}
              >
                <CardHeader className="pb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 mb-2">
                    <PenTool className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg font-bold text-gray-900">Expression Écrite (EE)</CardTitle>
                  <CardDescription className="text-xs text-emerald-700 font-semibold uppercase tracking-wider mt-0.5">
                    {t("dashboard.studyNow", "Escrita")}
                  </CardDescription>
                  <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                    {t("startTest.writingDesc", "Produção escrita com tarefas oficiais de redação.")}
                  </p>
                </CardHeader>
              </Card>
            </div>
          </div>

          {/* Seção 3: Escolha a seção específica */}
          {selectedCompetence && (
            <div className="space-y-4 animate-in slide-in-from-top-4 duration-300">
              <h2 className="text-lg font-bold text-gray-800">{t("startTest.sectionStepTitle", "3. Escolha a seção")}</h2>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {/* Opção Todas as Seções */}
                <Card 
                  onClick={() => setSelectedSection("all")}
                  className={cn(
                    "cursor-pointer border transition-all duration-200 p-4 rounded-xl flex items-center justify-between",
                    selectedSection === "all" ? "border-primary bg-primary/10 shadow-sm font-bold text-primary" : "border-gray-200 bg-white hover:border-gray-300 text-gray-800"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Layers className="w-5 h-5 text-primary shrink-0" />
                    <span className="text-sm">
                      {selectedCompetence === "writing" ? t("startTest.fullTestEE", "Teste completo EE") : t("startTest.allSections", "Todas as seções")}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {t("startTest.questionsCount", "{{count}} q", { count: selectedCompetence === "reading" ? readingCount : selectedCompetence === "listening" ? listeningCount : writingCount })}
                  </Badge>
                </Card>

                {/* Lista dinâmica de seções */}
                {(selectedCompetence === "reading" ? readingSections : selectedCompetence === "listening" ? listeningSections : writingSections).map((sec, idx) => {
                  const isSelected = selectedSection === sec.section;
                  const isMissing = sec.count === 0;

                  return (
                    <Card 
                      key={idx}
                      onClick={() => setSelectedSection(sec.section)}
                      className={cn(
                        "cursor-pointer border transition-all duration-200 p-4 rounded-xl flex items-center justify-between",
                        isSelected 
                          ? "border-primary bg-primary/10 shadow-sm font-bold text-primary" 
                          : isMissing 
                            ? "border-gray-100 bg-gray-50/50 opacity-60 text-gray-400" 
                            : "border-gray-200 bg-white hover:border-gray-300 text-gray-800"
                      )}
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <FileText className="w-4 h-4 shrink-0 opacity-70" />
                        <span className="text-xs truncate" title={sec.section}>
                          {sec.section}
                        </span>
                      </div>
                      <Badge variant="outline" className={cn("text-[10px] shrink-0 ml-2", isMissing ? "text-amber-600 bg-amber-50" : "text-gray-600")}>
                        {t("startTest.questionsCount", "{{count}} q", { count: sec.count })}
                      </Badge>
                    </Card>
                  );
                })}
              </div>

              {/* Alerta de seção vazia */}
              {selectedSection && availableCount === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-center text-amber-800 text-xs animate-in fade-in duration-300">
                  <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                  <span>
                    {selectedCompetence === "writing" ? t("startTest.emptyWritingAlert", "Nenhuma tarefa de escrita disponível no momento.") : t("startTest.emptySectionAlert", "Nenhuma questão disponível para esta seção no momento.")}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Seção 4: Modo de treino */}
          {/* Seção 4: Modo de treino */}
          {selectedCompetence && selectedSection && (
            <div className="space-y-4 animate-in slide-in-from-top-4 duration-300">
              <h2 className="text-lg font-bold text-gray-800">{t("startTest.trainingModeStepTitle", "4. Modo de treino")}</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <Card 
                  onClick={() => setTrainingMode("practice")}
                  className={cn(
                    "cursor-pointer border transition-all duration-200 p-4 rounded-xl",
                    trainingMode === "practice" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                  )}
                >
                  <h3 className="text-sm font-bold text-gray-900">{t("startTest.modePractice", "Treino livre")}</h3>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {t("startTest.modePracticeDesc", "Pratique com mais flexibilidade, ideal para estudo diário e assimilação.")}
                  </p>
                </Card>

                <Card 
                  onClick={() => setTrainingMode("exam")}
                  className={cn(
                    "cursor-pointer border transition-all duration-200 p-4 rounded-xl",
                    trainingMode === "exam" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                  )}
                >
                  <h3 className="text-sm font-bold text-gray-900">{t("startTest.modeExam", "Modo prova")}</h3>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {t("startTest.modeExamDesc", "Simula uma experiência mais próxima do exame oficial, com restrições.")}
                  </p>
                </Card>

                {!isWritingCompetency(selectedCompetence) && (
                  <Card 
                    onClick={() => setTrainingMode("diagnostic")}
                    className={cn(
                      "cursor-pointer border transition-all duration-200 p-4 rounded-xl",
                      trainingMode === "diagnostic" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                    )}
                  >
                    <h3 className="text-sm font-bold text-gray-900">{t("startTest.modeDiagnostic", "Diagnóstico")}</h3>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      {t("startTest.modeDiagnosticDesc", "Teste rápido para avaliar seu nível de proficiência.")}
                    </p>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Seção 5: Quantidade de questões (Apenas CE e CO, e se no modo treino livre) */}
          {selectedCompetence && selectedCompetence !== "writing" && selectedSection && trainingMode === "practice" && (
            <div className="space-y-4 animate-in slide-in-from-top-4 duration-300">
              <h2 className="text-lg font-bold text-gray-800">{t("startTest.questionCountStepTitle", "5. Quantidade de questões")}</h2>
              <div className="flex flex-wrap gap-3">
                {[5, 10, 20, "all"].map((cnt, idx) => {
                  const isSelected = questionCount === cnt;
                  const label = cnt === "all" ? t("startTest.allAvailable", "Todas disponíveis") : t("startTest.questionsCountLabel", "{{count}} questões", { count: cnt as number });

                  return (
                    <button
                      key={idx}
                      onClick={() => setQuestionCount(cnt as any)}
                      className={cn(
                        "px-5 py-3 rounded-xl border text-xs font-bold transition-all duration-200",
                        isSelected ? "bg-primary text-white border-primary shadow-md" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {typeof questionCount === "number" && availableCount > 0 && availableCount < questionCount && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 inline-flex items-center gap-2 mt-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  {t("startTest.fewQuestionsAlert", "Esta seção possui apenas {{count}} questões disponíveis no momento. Usaremos o máximo disponível.", { count: availableCount })}
                </p>
              )}
            </div>
          )}

          {/* Mensagem informativa para Modo Prova / Diagnóstico */}
          {selectedCompetence && selectedCompetence !== "writing" && selectedSection && trainingMode !== "practice" && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 space-y-2 animate-in fade-in duration-500">
              <div className="flex items-center gap-2 text-primary font-bold text-sm">
                <Sparkles className="w-4 h-4" />
                <span>{t("startTest.autoSelectionTitle", "Seleção Automática Ativada")}</span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                {trainingMode === "exam" 
                  ? t("startTest.autoSelectionExamDesc", "No Modo Prova, a quantidade e a sequência de questões seguem rigorosamente a estrutura oficial do exame TEF Canada para garantir um simulado fiel.")
                  : t("startTest.autoSelectionDiagDesc", "No Modo Diagnóstico, o sistema seleciona uma amostra equilibrada de questões de diferentes níveis e subtipos para avaliar sua proficiência de forma precisa.")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* --- FLUXO: SIMULADO COMPLETO --- */}
      {trainingType === "full_exam" && (
        <div className="space-y-8 animate-in slide-in-from-top-4 duration-300 pt-2 border-t border-gray-200">
          <Card className="border-none bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-700 text-white p-6 md:p-8 rounded-2xl shadow-lg relative overflow-hidden">
            <div className="absolute right-0 top-0 bottom-0 opacity-10 pointer-events-none flex items-center p-8 select-none">
              <Trophy className="w-64 h-64" />
            </div>
            <div className="relative z-10 max-w-2xl">
              <div className="flex items-center gap-2 text-xs font-black bg-white/20 text-white px-3 py-1 rounded-full w-fit mb-4 backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5" />
                {t("startTest.simulatedTitle", "Simulado Oficial Completo")}
              </div>
              <h2 className="text-2xl font-black tracking-tight leading-tight">{t("startTest.simulatedTitle", "Simulado Oficial Completo")}</h2>
              <p className="text-purple-100 text-sm mt-2 font-medium leading-relaxed">
                {t("startTest.simulatedDesc", "Você fará uma sequência oficial contínua com Compréhension Écrite, Compréhension Orale e Expression Écrite para simular as exigências de tempo e pressão reais.")}
              </p>
            </div>
          </Card>

          {/* Formato do Simulado */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-800">{t("startTest.simulatedFormatStepTitle", "2. Formato do Simulado")}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Card 
                onClick={() => setSimulationMode("short")}
                className={cn(
                  "cursor-pointer border transition-all duration-200 p-5 rounded-xl",
                  simulationMode === "short" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                )}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900">{t("startTest.simulatedShort", "Simulado curto")}</h3>
                  {simulationMode === "short" && <Check className="w-5 h-5 text-primary" />}
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  {t("startTest.simulatedShortDesc", "Versão enxuta com poucas questões de CE e CO e 1 tarefa de EE, excelente para praticar o fluxo completo em menos tempo.")}
                </p>
              </Card>

              <Card 
                onClick={() => setSimulationMode("standard")}
                className={cn(
                  "cursor-pointer border transition-all duration-200 p-5 rounded-xl",
                  simulationMode === "standard" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                )}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-gray-900">{t("startTest.simulatedStandard", "Simulado padrão")}</h3>
                  {simulationMode === "standard" && <Check className="w-5 h-5 text-primary" />}
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  {t("startTest.simulatedStandardDesc", "Versão regulamentar completa com as 40 questões de CE, 40 de CO e as 2 tarefas oficiais de EE.")}
                </p>
              </Card>
            </div>
          </div>

          {/* Modo de treino */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-800">{t("startTest.simulatedModeStepTitle", "3. Modo de treino")}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <Card 
                onClick={() => setTrainingMode("practice")}
                className={cn(
                  "cursor-pointer border transition-all duration-200 p-4 rounded-xl",
                  trainingMode === "practice" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                )}
              >
                <h3 className="text-sm font-bold text-gray-900">{t("startTest.modePractice", "Treino livre")}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t("startTest.modePracticeDesc", "Mais flexibilidade com navegação livre.")}</p>
              </Card>

              <Card 
                onClick={() => setTrainingMode("exam")}
                className={cn(
                  "cursor-pointer border transition-all duration-200 p-4 rounded-xl",
                  trainingMode === "exam" ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 bg-white"
                )}
              >
                <h3 className="text-sm font-bold text-gray-900">{t("startTest.modeExam", "Modo prova")}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{t("startTest.modeExamDesc", "Simula a experiência do exame com tempo rígido.")}</p>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* --- RESUMO E BOTÃO PRINCIPAL --- */}
      {canStart() && (
        <div className="space-y-6 pt-6 border-t border-gray-200 animate-in fade-in duration-300">
          <Card className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm">
            <h3 className="text-sm font-black uppercase text-slate-500 tracking-wider mb-4">{t("startTest.summaryHeading", "Você vai iniciar:")}</h3>
            
            {trainingType === "competence" && selectedCompetence && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.competenceSummaryLabel", "Competência")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block">{getCompetencyLabel(selectedCompetence)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.sectionSummaryLabel", "Seção")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block truncate" title={selectedSection || ""}>
                    {selectedSection === "all" ? (selectedCompetence === "writing" ? t("startTest.fullTestEE", "Teste completo EE") : t("startTest.allSections", "Todas as seções")) : selectedSection}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.modeSummaryLabel", "Modo")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block capitalize">
                    {trainingMode === "practice" ? t("startTest.modePractice", "Treino livre") : trainingMode === "exam" ? t("startTest.modeExam", "Modo prova") : t("startTest.modeDiagnostic", "Diagnóstico")}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.qtySummaryLabel", "Quantidade")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block">
                    {selectedCompetence === "writing" ? (
                      selectedSection === "all" ? t("startTest.qtyTasks", "{{count}} tarefas", { count: 2 }) : t("startTest.qtyTask", "1 tarefa")
                    ) : trainingMode === "exam" ? (
                      t("startTest.qtyOfficial", "Estrutura oficial")
                    ) : trainingMode === "diagnostic" ? (
                      t("startTest.qtyAuto", "Automática pelo diagnóstico")
                    ) : (
                      questionCount === "all" ? t("startTest.allAvailable", "Todas disponíveis") : t("startTest.questionsCountLabel", "{{count}} questões", { count: questionCount })
                    )}
                  </span>
                </div>
              </div>
            )}

            {trainingType === "full_exam" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.typeSummaryLabel", "Tipo")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block">{t("startTest.fullExamTitle", "Simulado Completo")}</span>
                </div>
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.formatSummaryLabel", "Formato")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block capitalize">{simulationMode === "short" ? t("startTest.simulatedShort", "Simulado curto") : t("startTest.simulatedStandard", "Simulado padrão")}</span>
                </div>
                <div>
                  <span className="text-slate-400 block font-semibold">{t("startTest.modeSummaryLabel", "Modo")}</span>
                  <span className="font-bold text-slate-800 text-sm mt-0.5 block capitalize">
                    {trainingMode === "practice" ? t("startTest.modePractice", "Treino livre") : trainingMode === "exam" ? t("startTest.modeExam", "Modo prova") : t("startTest.modeDiagnostic", "Diagnóstico")}
                  </span>
                </div>
              </div>
            )}
          </Card>

          <Button 
            onClick={() => {
              // Prevenção extra para EE não iniciar como diagnóstico
              if (isWritingCompetency(selectedCompetence) && trainingMode === "diagnostic") {
                setTrainingMode("exam");
                setTimeout(handleStartTest, 10);
              } else {
                handleStartTest();
              }
            }}
            disabled={!canStart() || availableCount === 0}
            className="w-full h-14 bg-primary hover:bg-primary/95 text-white font-black rounded-xl text-base shadow-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.01]"
          >
            <Play className="w-5 h-5 fill-white" />
            {t("startTest.startTestButton", "Iniciar Teste Agora")}
            <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
